import React, { useState } from 'react';
import { organizationService } from '../../services/organizationService';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, Save, Building2, CheckCircle2, KeyRound } from 'lucide-react';

const OrganizationForm = ({ onCreated, onCancel }) => {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: '',
        orgType: 'private',
        seatCount: 10,
        billingPeriod: 'yearly',
        dealAmount: '',
        dealCurrency: 'TRY',
        dealNotes: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        startsAt: new Date().toISOString().slice(0, 10),
        endsAt: ''
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const response = await organizationService.createOrganization({
                ...form,
                seatCount: parseInt(form.seatCount, 10),
                dealAmount: form.dealAmount ? parseFloat(form.dealAmount) : null,
                endsAt: form.endsAt || null
            });
            if (response.success) {
                setResult(response.data);
            } else {
                setError(response.message || t('createOrganizationError'));
            }
        } catch (err) {
            setError(err.response?.data?.message || t('serverError'));
        } finally {
            setLoading(false);
        }
    };

    if (result) {
        return (
            <div className="organization-form-container">
                <div className="form-header">
                    <h2><CheckCircle2 size={24} /> {t('organizationCreated')}</h2>
                </div>

                <div className="organization-form">
                    <p className="form-subtitle">{t('organizationCreatedDescription')}</p>

                    <div className="credentials-card">
                        <div className="credential-row">
                            <span className="credential-label">{t('email')}</span>
                            <span className="credential-value">{result.admin.email}</span>
                        </div>
                        <div className="credential-row">
                            <span className="credential-label">{t('password')}</span>
                            <span className="credential-value">{result.admin.password}</span>
                        </div>
                        <div className="credential-row">
                            <span className="credential-label"><KeyRound size={14} /> {t('licenseKey')}</span>
                            <span className="credential-value">{result.admin.licenseKey}</span>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="save-btn" onClick={onCreated}>
                            {t('backToOrganizationListButton')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="organization-form-container">
            <div className="form-header">
                <button className="back-btn" onClick={onCancel}>
                    <ArrowLeft size={20} />
                    {t('cancel')}
                </button>
                <h2><Building2 size={24} /> {t('createOrganizationTitle')}</h2>
            </div>
            <p className="form-subtitle">{t('createOrganizationDescription')}</p>

            <form onSubmit={handleSubmit} className="organization-form">
                {error && <div className="form-error">{error}</div>}

                <div className="form-section-title">{t('organizationDetailsSection')}</div>

                <div className="form-group">
                    <label>{t('organizationName')}</label>
                    <input type="text" value={form.name} onChange={update('name')} required />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>{t('organizationType')}</label>
                        <select value={form.orgType} onChange={update('orgType')}>
                            <option value="private">{t('typePrivate')}</option>
                            <option value="government">{t('typeGovernment')}</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>{t('seatCount')}</label>
                        <input type="number" min="1" value={form.seatCount} onChange={update('seatCount')} required />
                    </div>
                </div>

                <div className="form-row form-row-3">
                    <div className="form-group">
                        <label>{t('billingPeriod')}</label>
                        <select value={form.billingPeriod} onChange={update('billingPeriod')}>
                            <option value="monthly">{t('monthly')}</option>
                            <option value="yearly">{t('yearly')}</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>{t('dealAmount')}</label>
                        <input type="number" step="0.01" value={form.dealAmount} onChange={update('dealAmount')} />
                    </div>
                    <div className="form-group">
                        <label>{t('currency')}</label>
                        <input type="text" value={form.dealCurrency} onChange={update('dealCurrency')} />
                    </div>
                </div>

                <div className="form-group">
                    <label>{t('notes')}</label>
                    <textarea value={form.dealNotes} onChange={update('dealNotes')} rows="3" />
                </div>

                <div className="form-section-title">{t('organizationAdminSection')}</div>

                <div className="form-row">
                    <div className="form-group">
                        <label>{t('managerName')}</label>
                        <input type="text" value={form.contactName} onChange={update('contactName')} />
                    </div>
                    <div className="form-group">
                        <label>{t('managerPhone')}</label>
                        <input type="text" value={form.contactPhone} onChange={update('contactPhone')} />
                    </div>
                </div>

                <div className="form-group">
                    <label>{t('managerEmail')}</label>
                    <input type="email" value={form.contactEmail} onChange={update('contactEmail')} required />
                </div>

                <div className="form-section-title">{t('contractSection')}</div>

                <div className="form-row">
                    <div className="form-group">
                        <label>{t('startDate')}</label>
                        <input type="date" value={form.startsAt} onChange={update('startsAt')} required />
                    </div>
                    <div className="form-group">
                        <label>{t('endDate')}</label>
                        <input type="date" value={form.endsAt} onChange={update('endsAt')} />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" className="cancel-btn" onClick={onCancel}>{t('cancel')}</button>
                    <button type="submit" className="save-btn" disabled={loading}>
                        <Save size={16} />
                        {loading ? t('creatingOrganization') : t('createOrganizationButton')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default OrganizationForm;
